/**
 * Adapter for array-style GraphQL errors
 */

import { mapErrorCodeToCategory, getUserFriendlyMessage, ErrorCategory } from '@core/domain/errors';
import type { DomainError } from '@core/domain/errors';

interface GraphQLErrorItem {
  message?: string;
  extensions?: {
    code?: string;
    details?: unknown;
    correlationId?: string;
  };
}

/**
 * Adapts an array-style GraphQL error to a domain error
 */
export function adaptArrayGraphQLError(error: unknown[]): DomainError[] {
  if (error.length === 0) {
    return [
      {
        code: 'UNKNOWN_ERROR',
        message: 'An unexpected error occurred',
        userMessage: 'Something went wrong. Please try again later.',
        category: ErrorCategory.INTERNAL,
      },
    ];
  }

  return error.map(item => {
    const graphQLError = item as GraphQLErrorItem;
    const code = graphQLError.extensions?.code || 'UNKNOWN_ERROR';
    const message = graphQLError.message || 'An unexpected error occurred';
    const category = mapErrorCodeToCategory(code);

    return {
      code,
      message,
      userMessage: getUserFriendlyMessage(category, message, code),
      category,
      details: graphQLError.extensions?.details,
      correlationId: graphQLError.extensions?.correlationId,
    };
  });
}

/**
 * Checks if the error is an array-style GraphQL error
 */
export function isArrayGraphQLError(error: unknown): error is unknown[] {
  return Array.isArray(error) && error.length > 0 && typeof error[0] === 'object' && error[0] !== null && 'extensions' in error[0];
}
